import React, { useCallback, useEffect, useMemo, useState } from "react";
import { apiDelete, apiGet } from "../../shared/apiClient";
import { useAuth } from "../auth/AuthContext";
import { useToast } from "../../shared/ToastContext";

const inputClass =
  "rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 outline-none focus:border-brand-600 focus:ring-2 focus:ring-brand-600/20";

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(date);
}

function formatMoney(value) {
  if (value == null || value === "") return "-";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function ProductEnquiriesPage() {
  const { user } = useAuth();
  const toast = useToast();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [customers, setCustomers] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(0);
  const [limit, setLimit] = useState(100);

  const queryCustomer = useMemo(
    () => (isAdmin && selectedCustomerId ? `customerId=${encodeURIComponent(selectedCustomerId)}` : ""),
    [isAdmin, selectedCustomerId]
  );

  useEffect(() => {
    const loadCustomers = async () => {
      if (!isAdmin) return;
      const res = await apiGet("/api/Customers");
      if (res.ok) setCustomers(Array.isArray(res.data) ? res.data : []);
    };
    void loadCustomers();
  }, [isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    const parts = [`limit=${encodeURIComponent(limit)}`];
    if (queryCustomer) parts.push(queryCustomer);
    const res = await apiGet(`/api/ProductEnquiries?${parts.join("&")}`);
    setLoading(false);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to load product enquiries"), "error");
      return;
    }
    setRows(Array.isArray(res.data) ? res.data : []);
  }, [limit, queryCustomer, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const removeRow = async (row) => {
    const confirmed = window.confirmDialog
      ? await window.confirmDialog("Delete this product enquiry?")
      : window.confirm("Delete this product enquiry?");
    if (!confirmed) return;
    setDeletingId(row.id);
    const res = await apiDelete(`/api/ProductEnquiries/${row.id}`);
    setDeletingId(0);
    if (!res.ok) {
      toast.push(String(res.error || "Failed to delete enquiry"), "error");
      return;
    }
    toast.push("Enquiry deleted", "success");
    await load();
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Product Enquiries</h1>
            <p className="mt-1 text-sm text-slate-500">Product enquiries received from customer websites.</p>
          </div>
          <button
            type="button"
            onClick={() => void load()}
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Refresh
          </button>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {isAdmin && (
            <select className={inputClass} value={selectedCustomerId} onChange={(e) => setSelectedCustomerId(e.target.value)}>
              <option value="">All customers</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.code || `Customer ${c.id}`}
                </option>
              ))}
            </select>
          )}
          <select className={inputClass} value={String(limit)} onChange={(e) => setLimit(Number(e.target.value) || 100)}>
            <option value="50">50 rows</option>
            <option value="100">100 rows</option>
            <option value="200">200 rows</option>
            <option value="500">500 rows</option>
          </select>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1600px] text-left text-sm">
              <thead>
                <tr className="bg-slate-50">
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Id</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Name</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Mob No</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Email</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Remarks</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Delivery Address</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Delivery Location</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Pin Code</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Booking Date</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Active</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Product</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Qty</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Rate</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Total</th>
                  <th className="px-4 py-3 text-xs font-semibold text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50/80">
                    <td className="px-4 py-3 text-slate-600">{row.id}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{row.name || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{row.mobileNo || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{row.email || "-"}</td>
                    <td className="max-w-[220px] px-4 py-3 text-slate-600">
                      <span className="block whitespace-pre-wrap">{row.remarks || "-"}</span>
                    </td>
                    <td className="max-w-[240px] px-4 py-3 text-slate-600">
                      <span className="block whitespace-pre-wrap">{row.deliveryAddress || "-"}</span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{row.deliveryLocation || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{row.deliveryPinCode || "-"}</td>
                    <td className="px-4 py-3 text-slate-600">{formatDate(row.bookingDate)}</td>
                    <td className="px-4 py-3 text-slate-600">{row.isActive ? "Yes" : "No"}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <p className="font-medium">{row.productName || "-"}</p>
                      <p className="text-xs text-slate-500">#{row.productId}</p>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{row.quantity ?? 1}</td>
                    <td className="px-4 py-3 text-slate-600">{formatMoney(row.rate)}</td>
                    <td className="px-4 py-3 font-medium text-slate-900">{formatMoney(row.total)}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => void removeRow(row)}
                        disabled={deletingId === row.id}
                        className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60"
                      >
                        {deletingId === row.id ? "Deleting..." : "Delete"}
                      </button>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={15} className="px-4 py-12 text-center text-sm text-slate-500">
                      No product enquiries found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
